import {FetchBaseQueryError} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {openSuccessDialog, closeCurrentDialog, showNotification} from 'platform/components';
import {Show, Spinner} from 'platform/foundation';

import {useState} from 'react';

import {isNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  useAssignOwnerFromTeamMutation,
  useDeleteTaskMutation,
  useEditTaskMutation,
  useGetTaskQuery,
} from '@dms/api/core';
import {EntityResourceIds, TaskStateEnumSchema} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {
  castTimeToDateISOString,
  getTeamId,
  handleApiError,
  isTeamValue,
  usePermissions,
} from '@dms/shared';

import {
  getApiDateString,
  noop,
  parseDate,
  pickRandomValueFromArr,
  RequiredTestIdProps,
  suffixTestId,
  useBooleanByChance,
} from 'shared/web';

import type {TaskForm as TaskFormType} from '../../types/TaskForm';
import {TaskRelationProps} from '../../types/TaskRelationProps';
import {TaskRelations} from '../../types/TaskRelations';
import {openTaskHistoryDialog} from '../../utils/openTaskHistoryDialog';
import {TaskForm} from '../TaskForm/TaskForm';

interface EditTaskProps extends RequiredTestIdProps, TaskRelationProps {
  id: string;
  onEdit?: () => void;
  onDelete?: () => void;
}

export function EditTask(props: EditTaskProps) {
  const [canDeleteTask, canEditTask] = usePermissions({
    permissionKeys: ['deleteTask', 'updateTask'],
  });

  const showSuccessDialog = useBooleanByChance(0.33);
  const successDialogHeading = pickRandomValueFromArr([
    i18n.t('entity.task.notifications.taskCompleted_1'),
    i18n.t('entity.task.notifications.taskCompleted_2'),
    i18n.t('entity.task.notifications.taskCompleted_3'),
    i18n.t('entity.task.notifications.taskCompleted_4'),
    i18n.t('entity.task.notifications.taskCompleted_5'),
  ]);
  const successDialogSubheading = pickRandomValueFromArr([
    i18n.t('entity.task.notifications.taskCompletedSub_1'),
    i18n.t('entity.task.notifications.taskCompletedSub_2'),
    i18n.t('entity.task.notifications.taskCompletedSub_3'),
    i18n.t('entity.task.notifications.taskCompletedSub_4'),
    i18n.t('entity.task.notifications.taskCompletedSub_5'),
  ]);

  const {
    data: task,
    isLoading: isTaskLoading,
    isError: hasTaskError,
    error: taskError,
    refetch: refetchTask,
  } = useGetTaskQuery({id: props.id});
  const [editTask] = useEditTaskMutation();
  const [deleteTask, {isLoading: isDeleteTaskLoading}] = useDeleteTaskMutation();
  const [assignOwnerFromTeam] = useAssignOwnerFromTeamMutation();
  /** From the first request of a save to its last: the team path reads the task between two writes. */
  const [isSaving, setIsSaving] = useState(false);

  if (hasTaskError) {
    if ((taskError as FetchBaseQueryError)?.status === 404) {
      showNotification.error(
        i18n.t('general.notifications.notFound', {
          item: i18n.t('entity.task.type.task'),
        })
      );
    } else {
      showNotification.error(i18n.t('general.labels.somethingWentWrong'));
    }
    closeCurrentDialog();
    return null;
  }

  const onEditTask = (data: TaskFormType) => {
    setIsSaving(true);
    const teamId = isTeamValue(data.assignedTo) ? getTeamId(data.assignedTo) : null;

    const handleEditSuccess = () => {
      closeCurrentDialog();
      if (
        task?.state !== TaskStateEnumSchema.enum.DONE &&
        data.state === TaskStateEnumSchema.enum.DONE &&
        showSuccessDialog
      ) {
        openSuccessDialog({
          id: 'edit-task-success-dialog',
          heading: successDialogHeading!,
          subheading: successDialogSubheading!,
          'data-testid': props['data-testid'],
          buttonLabel: i18n.t('general.actions.close'),
          onClose: closeCurrentDialog,
        });
      }
      props.onEdit?.();
    };

    if (isNil(teamId)) {
      return editTask({
        id: props.id,
        editTaskRequestBody: {
          ...data,
          assignedTo: data.assignedTo,
          dueDate: data?.dueDate ? getApiDateString(parseDate(data.dueDate)) : null,
          dueFrom:
            data?.dueDate && data?.dueFrom
              ? castTimeToDateISOString(data.dueDate, data.dueFrom)
              : null,
          dueTo:
            data?.dueDate && data?.dueTo ? castTimeToDateISOString(data.dueDate, data.dueTo) : null,
        },
      })
        .unwrap()
        .then(handleEditSuccess)
        .catch(handleApiError)
        .finally(() => setIsSaving(false));
    }

    return assignOwnerFromTeam({
      resourceId: EntityResourceIds.task,
      recordId: props.id,
      assignOwnerFromTeamRequestBody: {teamId},
    })
      .unwrap()
      .then(() => refetchTask().unwrap())
      .then((refetchedTask) =>
        editTask({
          id: props.id,
          editTaskRequestBody: {
            ...data,
            assignedTo: refetchedTask.assignedTo,
            dueDate: data?.dueDate ? getApiDateString(parseDate(data.dueDate)) : null,
            dueFrom:
              data?.dueDate && data?.dueFrom
                ? castTimeToDateISOString(data.dueDate, data.dueFrom)
                : null,
            dueTo:
              data?.dueDate && data?.dueTo
                ? castTimeToDateISOString(data.dueDate, data.dueTo)
                : null,
          },
        })
          .unwrap()
          .then(handleEditSuccess)
      )
      .catch(handleApiError)
      .finally(() => setIsSaving(false));
  };

  const onDeleteTask = () =>
    deleteTask({id: props.id})
      .unwrap()
      .then(() => {
        closeCurrentDialog();
        props.onDelete?.();
        showNotification.success(i18n.t('general.notifications.successfullyDeleted'));
      })
      .catch(handleApiError);

  return (
    <>
      <Show when={isNotNilOrEmpty(task)}>
        <TaskForm
          isCreateTask={false}
          values={task}
          onSubmit={onEditTask}
          isReadOnly={!canEditTask}
          onDelete={canDeleteTask ? onDeleteTask : undefined}
          // The history names the automation rule behind a change (RQ_18), so it
          // ships behind the same flag as the rules.
          isHistoryDisabled={isSaving || isDeleteTaskLoading}
          onShowHistory={
            isFeatureEnabled(featureFlags.ORCHESTRATION_MODULE)
              ? () => openTaskHistoryDialog({taskId: props.id, 'data-testid': props['data-testid']})
              : undefined
          }
          isLoading={isDeleteTaskLoading}
          data-testid={suffixTestId('editTask', props)}
          relatedRecordId={task?.relatedRecordId?.id}
          resourceId={(task?.relatedRecordId?.resourceId as TaskRelations) ?? props.resourceId}
        />
      </Show>
      <Show when={isTaskLoading}>
        <TaskForm
          isCreateTask={false}
          onSubmit={noop}
          data-testid={suffixTestId('skeletonTask', props)}
        />
        <Spinner variant="overlay" />
      </Show>
    </>
  );
}
